import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Form,
  Table,
  Tag,
  Dropdown,
  Collapse,
  Space,
  Switch,
  InputNumber,
  Empty,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { IconArrowRight } from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import ConfirmModal from "../../components/modal/ConfirmModal";
import ImageUpload from "../../components/image-upload/ImageUpload";
import api from "../../api";

export default function HomeProducts() {
  const toast = useToast();

  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tabModal, setTabModal] = useState({ open: false, row: null });
  const [cardModal, setCardModal] = useState({
    open: false,
    row: null,
    tabId: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: null,
    id: null,
  });
  const [deleting, setDeleting] = useState(false);

  const [tabForm] = Form.useForm();
  const [cardForm] = Form.useForm();

  // ─── Fetch ───────────────────────────────────────────
  const fetchTabs = useCallback(async () => {
    setLoading(true);
    const res = await api.home_products_tabs();
    if (res?.status === 200) {
      setTabs(res.data.data || []);
    } else {
      toast("Өгөгдөл авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTabs();
  }, [fetchTabs]);

  // ─── Tab ─────────────────────────────────────────────
  const openTabModal = (row = null) => {
    tabForm.resetFields();
    if (row) {
      tabForm.setFieldsValue({
        name: row.name,
        iconUrl: row.iconUrl || null,
        sortOrder: row.sortOrder,
        isActive: row.isActive,
      });
    } else {
      tabForm.setFieldsValue({ isActive: true });
    }
    setTabModal({ open: true, row });
  };

  const onTabSubmit = async () => {
    try {
      const values = await tabForm.validateFields();
      setSaving(true);

      const payload = {
        name: values.name.trim(),
        iconUrl: values.iconUrl || null,
        isActive: values.isActive ?? true,
        ...(values.sortOrder !== undefined && { sortOrder: values.sortOrder }),
      };

      const res = tabModal.row
        ? await api.home_products_tab_update(tabModal.row.id, payload)
        : await api.home_products_tab_create(payload);

      if (res?.status === 200 || res?.status === 201) {
        toast(
          tabModal.row ? "Амжилттай засагдлаа" : "Амжилттай нэмэгдлээ",
          "success",
        );
        setTabModal({ open: false, row: null });
        fetchTabs();
      } else {
        toast(res?.data?.message || "Алдаа гарлаа", "error");
      }
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const moveTab = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= tabs.length) return;

    const next = [...tabs];
    [next[index], next[target]] = [next[target], next[index]];
    setTabs(next);

    const res = await api.home_products_tabs_reorder(
      next.map((t, i) => ({ id: t.id, sortOrder: i })),
    );
    if (res?.status !== 200) {
      toast("Дараалал хадгалахад алдаа гарлаа", "error");
      fetchTabs();
    }
  };

  // ─── Card ────────────────────────────────────────────
  const openCardModal = (tabId, row = null) => {
    cardForm.resetFields();
    if (row) {
      cardForm.setFieldsValue({
        title: row.title,
        href: row.href || "",
        rows: Array.isArray(row.rows) ? row.rows : [],
        sortOrder: row.sortOrder,
        isActive: row.isActive,
      });
    } else {
      cardForm.setFieldsValue({ isActive: true, rows: [{}] });
    }
    setCardModal({ open: true, row, tabId });
  };

  const onCardSubmit = async () => {
    try {
      const values = await cardForm.validateFields();
      setSaving(true);

      const payload = {
        tabId: cardModal.tabId,
        title: values.title.trim(),
        href: values.href?.trim() || null,
        rows: values.rows || [],
        isActive: values.isActive ?? true,
        ...(values.sortOrder !== undefined && { sortOrder: values.sortOrder }),
      };

      const res = cardModal.row
        ? await api.home_products_card_update(cardModal.row.id, payload)
        : await api.home_products_card_create(payload);

      if (res?.status === 200 || res?.status === 201) {
        toast(
          cardModal.row ? "Амжилттай засагдлаа" : "Амжилттай нэмэгдлээ",
          "success",
        );
        setCardModal({ open: false, row: null, tabId: null });
        fetchTabs();
      } else {
        toast(res?.data?.message || "Алдаа гарлаа", "error");
      }
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const moveCard = async (tabId, index, dir) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab) return;

    const target = index + dir;
    if (target < 0 || target >= tab.cards.length) return;

    const nextCards = [...tab.cards];
    [nextCards[index], nextCards[target]] = [
      nextCards[target],
      nextCards[index],
    ];
    setTabs((prev) =>
      prev.map((t) => (t.id === tabId ? { ...t, cards: nextCards } : t)),
    );

    const res = await api.home_products_cards_reorder(
      nextCards.map((c, i) => ({ id: c.id, sortOrder: i })),
    );
    if (res?.status !== 200) {
      toast("Дараалал хадгалахад алдаа гарлаа", "error");
      fetchTabs();
    }
  };

  // ─── Delete ──────────────────────────────────────────
  const onDelete = async () => {
    setDeleting(true);
    const res =
      deleteModal.type === "tab"
        ? await api.home_products_tab_delete(deleteModal.id)
        : await api.home_products_card_delete(deleteModal.id);

    if (res?.status === 200) {
      toast("Устгагдлаа", "info");
      fetchTabs();
    } else {
      toast(res?.data?.message || "Устгахад алдаа гарлаа", "error");
    }
    setDeleting(false);
    setDeleteModal({ open: false, type: null, id: null });
  };

  // ─── Card columns ────────────────────────────────────
  const cardColumns = (tabId, cards) => [
    {
      title: "",
      key: "order",
      width: 90,
      render: (_, __, index) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={() => moveCard(tabId, index, -1)}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === cards.length - 1}
            onClick={() => moveCard(tabId, index, 1)}
          />
        </Space>
      ),
    },
    {
      title: "Гарчиг",
      dataIndex: "title",
      render: (v) => <div className="fw-semibold">{v}</div>,
    },
    {
      title: "Мөрүүд",
      dataIndex: "rows",
      render: (v) => (
        <div style={{ fontSize: 12, color: "#666" }}>
          {(Array.isArray(v) ? v : []).map((r, i) => (
            <div key={i}>
              {r.label} — <strong>{r.value}</strong>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Холбоос",
      dataIndex: "href",
      width: 200,
      render: (v) =>
        v ? (
          <code style={{ fontSize: 12 }}>{v}</code>
        ) : (
          <span style={{ color: "#ccc" }}>—</span>
        ),
    },
    {
      title: "Төлөв",
      dataIndex: "isActive",
      width: 100,
      render: (v) =>
        v ? <Tag color="green">Идэвхтэй</Tag> : <Tag>Идэвхгүй</Tag>,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openCardModal(tabId, record);
              if (key === "delete")
                setDeleteModal({ open: true, type: "card", id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  // ─── Collapse items ──────────────────────────────────
  const collapseItems = tabs.map((tab, index) => ({
    key: tab.id,
    label: (
      <div className="d-flex align-items-center gap-2">
        {tab.iconUrl ? (
          <img
            src={tab.iconUrl}
            alt=""
            style={{ width: 24, height: 24, objectFit: "contain" }}
          />
        ) : (
          <span style={{ color: "#ccc", fontSize: 12 }}>icon×</span>
        )}
        <span className="fw-semibold">{tab.name}</span>
        <Tag>{tab.cards?.length || 0} карт</Tag>
        {!tab.isActive && <Tag color="default">Идэвхгүй</Tag>}
      </div>
    ),
    extra: (
      <Space size={4} onClick={(e) => e.stopPropagation()}>
        <Button
          size="small"
          icon={<ArrowUpOutlined />}
          disabled={index === 0}
          onClick={() => moveTab(index, -1)}
        />
        <Button
          size="small"
          icon={<ArrowDownOutlined />}
          disabled={index === tabs.length - 1}
          onClick={() => moveTab(index, 1)}
        />
        <Button size="small" onClick={() => openTabModal(tab)}>
          Засах
        </Button>
        <Button
          size="small"
          danger
          onClick={() =>
            setDeleteModal({ open: true, type: "tab", id: tab.id })
          }
        >
          Устгах
        </Button>
      </Space>
    ),
    children: (
      <>
        <div className="d-flex justify-content-end mb-2">
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openCardModal(tab.id)}
          >
            Карт нэмэх
          </Button>
        </div>
        <Table
          rowKey="id"
          size="small"
          columns={cardColumns(tab.id, tab.cards || [])}
          dataSource={tab.cards || []}
          pagination={false}
          locale={{ emptyText: <Empty description="Карт байхгүй" /> }}
        />
      </>
    ),
  }));

  return (
    <section className="staff-section">
      <PageHeader
        title="Нүүр бүтээгдэхүүн"
        sub="Нүүр хуудасны жишээ бүтээгдэхүүний таб, карт"
        rSide={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openTabModal()}
          >
            Таб нэмэх
          </Button>
        }
      />

      <div className="table-wrap">
        {tabs.length === 0 && !loading ? (
          <Empty description="Таб байхгүй байна" />
        ) : (
          <Collapse items={collapseItems} accordion />
        )}
      </div>

      {/* Tab Modal */}
      <Modal
        open={tabModal.open}
        onCancel={() => setTabModal({ open: false, row: null })}
        onOk={onTabSubmit}
        okText={tabModal.row ? "Хадгалах" : "Нэмэх"}
        cancelText="Болих"
        confirmLoading={saving}
        title={tabModal.row ? "Таб засах" : "Таб нэмэх"}
      >
        <Form form={tabForm} layout="vertical">
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэрээ оруулна уу" }]}
          >
            <Input placeholder="Дижитал зээл" />
          </Form.Item>

          <Form.Item label="Icon" name="iconUrl">
            <ImageUpload />
          </Form.Item>

          <Form.Item label="Дараалал" name="sortOrder">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Идэвхтэй" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Card Modal */}
      <Modal
        open={cardModal.open}
        onCancel={() => setCardModal({ open: false, row: null, tabId: null })}
        onOk={onCardSubmit}
        okText={cardModal.row ? "Хадгалах" : "Нэмэх"}
        cancelText="Болих"
        confirmLoading={saving}
        title={cardModal.row ? "Карт засах" : "Карт нэмэх"}
        width={640}
      >
        <Form form={cardForm} layout="vertical">
          <Form.Item
            label="Гарчиг"
            name="title"
            rules={[{ required: true, message: "Гарчиг оруулна уу" }]}
          >
            <Input placeholder="Аппын зээл" />
          </Form.Item>

          <Form.Item
            label="Холбоос"
            name="href"
            tooltip="Сумны товч дарахад очих хаяг. Хоосон бол сум харагдахгүй."
          >
            <Input placeholder="/loan" />
          </Form.Item>

          <Form.Item label="Мөрүүд" required>
            <Form.List name="rows">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <div
                      key={key}
                      className="d-flex gap-2 align-items-start mb-2"
                    >
                      <Form.Item
                        {...rest}
                        name={[name, "label"]}
                        rules={[{ required: true, message: "Нэр оруулна уу" }]}
                        style={{ marginBottom: 0, flex: 1 }}
                      >
                        <Input placeholder="Санал болгох хүү (1 сар)" />
                      </Form.Item>
                      <Form.Item
                        {...rest}
                        name={[name, "value"]}
                        rules={[{ required: true, message: "Утга оруулна уу" }]}
                        style={{ marginBottom: 0, width: 140 }}
                      >
                        <Input placeholder="2.80%" />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => add()}
                  >
                    Мөр нэмэх
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label="Дараалал" name="sortOrder">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Идэвхтэй" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        loading={deleting}
        title="Устгах уу?"
        description={
          deleteModal.type === "tab"
            ? "Табыг устгахад доторх бүх карт хамт устана."
            : "Энэ картыг устгах уу?"
        }
        variant="warning"
      />
    </section>
  );
}
